import { z } from 'zod';
import type { DataUpdateGroupId } from '../../../../shared/dpRequests';

const MARITAL_STATUS_WITH_SPOUSE = ['Casado(a)', 'União Estável'];

const optionalText = z.string().nullable().optional();

const emailField = z
    .string()
    .trim()
    .email('Informe um e-mail válido')
    .or(z.literal(''))
    .nullable()
    .optional();

const phoneField = optionalText.refine(
    value => !value || value.replace(/\D/g, '').length >= 10,
    'Informe o telefone com DDD'
);

const addressSchema = z
    .array(
        z
            .object({
                street: z.string().trim().min(1, 'Informe a rua'),
                city: z.string().trim().min(1, 'Informe a cidade'),
                state: z.string().trim().min(1, 'Informe o estado'),
                zipCode: z.string().trim().min(1, 'Informe o CEP'),
            })
            .passthrough()
    )
    .min(1, 'Informe o endereço');

const contactShape = {
    personalEmail: emailField,
    phone: phoneField,
    emergencyContact: z
        .object({
            name: optionalText,
            phone: optionalText,
            relationship: optionalText,
        })
        .passthrough()
        .nullable()
        .optional(),
};

const maritalShape = {
    maritalStatus: z.enum(['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'], {
        errorMap: () => ({ message: 'Escolha o estado civil' }),
    }),
    spouseName: optionalText,
};

const childrenSchema = z.array(
    z
        .object({
            name: z.string().trim().min(1, 'Informe o nome do filho'),
            birthDate: z.string().min(1, 'Informe a data de nascimento'),
        })
        .passthrough()
);

const bankAccountSchema = z
    .object({
        bankCode: optionalText,
        agency: optionalText,
        account: optionalText,
        digit: optionalText,
        pixKey: optionalText,
    })
    .passthrough()
    .nullable()
    .optional();

const educationSchema = z.array(
    z
        .object({
            type: z.string().min(1, 'Escolha o nível'),
            institution: z.string().trim().min(1, 'Informe a instituição'),
        })
        .passthrough()
);

const SHAPES: Record<DataUpdateGroupId, z.ZodRawShape> = {
    address: {
        addresses: addressSchema,
        isMailingSameAsMain: z.boolean().nullable().optional(),
    },
    contact: contactShape,
    maritalStatus: maritalShape,
    children: { children: childrenSchema },
    bankAccount: {
        salaryAccount: bankAccountSchema,
        personalAccount: bankAccountSchema,
    },
    education: { educations: educationSchema },
};

function hasAnyBankField(account: Record<string, unknown> | null | undefined): boolean {
    if (!account) return false;
    return ['bankCode', 'agency', 'account'].some(key => String(account[key] ?? '').trim() !== '');
}

export function buildDataUpdateSchema(groups: readonly DataUpdateGroupId[]) {
    const shape = groups.reduce<z.ZodRawShape>((all, group) => ({ ...all, ...SHAPES[group] }), {});

    return z
        .object(shape)
        .passthrough()
        .superRefine((data, ctx) => {
            if (groups.includes('maritalStatus')) {
                const status = String((data as Record<string, unknown>).maritalStatus ?? '');
                const spouse = String((data as Record<string, unknown>).spouseName ?? '').trim();
                if (MARITAL_STATUS_WITH_SPOUSE.includes(status) && spouse === '') {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['spouseName'], message: 'Informe o nome do cônjuge' });
                }
            }

            if (groups.includes('bankAccount')) {
                const record = data as Record<string, Record<string, unknown> | null | undefined>;
                (['salaryAccount', 'personalAccount'] as const).forEach(key => {
                    const account = record[key];
                    if (!hasAnyBankField(account)) return;
                    (['bankCode', 'agency', 'account'] as const).forEach(field => {
                        if (String(account?.[field] ?? '').trim() === '') {
                            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key, field], message: 'Campo obrigatório para a conta' });
                        }
                    });
                });
            }
        });
}
